/* eslint-disable @next/next/no-html-link-for-pages */
import axios from "axios";
import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CreateModeButton from "./create/CreateModeButton";
import { Spinner } from "./Spinner";
import { PlusIcon } from "@heroicons/react/20/solid";
import { CheckCircleIcon, CheckIcon, XCircleIcon } from "@heroicons/react/24/solid";

const sleepPromise = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

const createImage = async (prompt, mode, auxMode, i, total) => {
    console.log("creating", i, total, prompt);
    const identifier = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    let error = null;
    axios.post(`/api/image/create`, {
        prompt,
        identifier,
        mode: mode?.toLowerCase()?.replace(/ /g, "-"),
        auxMode: auxMode,
        bulk: `${i + 1}/${total}`,
    }).catch(err => {
        error = err;
    });
    await sleepPromise(5000);
    if (error) {
        throw error;
    }
    for (let i = 0; i < 120; i++) {
        const result = await axios.post(`/api/image/status`, {
            identifier,
        });
        console.log("status", result?.data?.status, i);
        if (result?.data?.status === "error") {
            throw new Error("Error creating image");
        }
        if (result?.data?.svg) {
            return result.data;
        }
        await sleepPromise(2000);
    }
    throw new Error("Timeout");
};

const createAllImages = async (rows, notify) => {
    const results = [...rows];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        row.status = "running";
        notify?.(results, i);
        if (_.trim(row.prompt) === "") {
            row.status = "skipped";
            notify?.(results, i);
            continue;
        }
        try {
            const data = await createImage(row.prompt, row.mode, row.auxMode, i, rows.length);
            row.status = "done";
            row.data = data;
        } catch (err) {
            row.status = "error";
            row.error = err.message;
            if (err?.response?.status === 401) {
                throw err;
            }
        }
        notify?.(results, i);
    }
    return results;
}

function RowStatus(props) {
    if (props.status === "done") {
        return <CheckCircleIcon className="w-6 h-6 text-green-500 dark:text-green-400 mr-3" />;
    } else if (props.status === "error") {
        return <XCircleIcon className="w-6 h-6 text-red-500 dark:text-red-400 mr-3" />;
    } else if (props.status === "running") {
        return <div><Spinner className="w-4 h-4 mr-3" /></div>;
    } else if (props.status === "skipped") {
        return <XCircleIcon className="w-6 h-6 text-gray-400 dark:text-gray-400 mr-3" />;
    }
    return null;
}

function InputRow(props) {
    return (
        <div className="w-full flex flex-col sm:flex-row sm:items-center fill-gray-900 dark:fill-gray-100 mt-2">
            {props.status && <RowStatus status={props.status} />}
            <div className="flex flex-1 flex-col sm:flex-row sm:items-center">
                <label
                    htmlFor={props.id}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-100"
                >
                    {props.label}
                </label>
                <div className="w-full flex-1 mr-3">
                    <input
                        autoFocus={true}
                        type="text"
                        disabled={props.disabled}
                        defaultValue={props.prompt}
                        onChange={(e) => props.setPrompt?.(e.target.value)}
                        className="block w-full rounded-md px-3 p-1.5 shadow-sm focus:outline-none dark:disabled:text-gray-400 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-gray-400 sm:text-sm sm:leading-6 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-gray-500 dark:ring-gray-600"
                    />
                </div>
            </div>
            <div className="mt-1 sm:mt-0">
                <CreateModeButton
                    position={props.position > 2 ? "bottom-full" : "top-full"}
                    disabled={props.disabled}
                    initialMode={props.mode}
                    initialAuxMode={props.auxMode}
                    selectedMode={props.mode}
                    selectedAuxMode={props.auxMode}
                    onSetMode={(mode, auxMode) => props.setSelectedMode?.(mode, auxMode)}
                />
            </div>
            {/* <div className="cursor-pointer" onClick={() => props.deleteRow?.()}><TrashIcon className="ml-3 w-4 h-4 text-gray-400 hover:text-gray-800" /></div> */}
        </div>
    );
}

export default function BulkCreateDialog(props) {
    const initialRow = {
        prompt: props.initialPrompt ?? "",
        mode: props.initialMode,
        auxMode: props.initialAuxMode,
    };
    const [open, setOpen] = useState(true);
    const [rows, setRows] = useState([initialRow]);
    const [isCreating, setIsCreating] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const sendNotificationRef = useRef(true);

    const go = () => {
        const newRows = rows.map(row => {
            return {
                ...row,
                status: null
            }
        });
        console.log("go", newRows);
        setIsCreating(true);
        createAllImages(newRows, (results, i) => {
            console.log("notify", results, i);
            setRows([...results]);
        }).catch(err => {
            if (err?.response?.status === 401) {
                props.setShowPurchase?.(true);
            }
        }).then((results) => {
            console.log("done", results, sendNotificationRef.current);
            props?.onCreate?.(results);
            if (sendNotificationRef.current && results?.filter(row => ["done", "error"].includes(row.status)).length > 0) {
                console.log("bulk done");
                return axios.get(`/api/image/bulk-done`);
            }
        }).finally(() => {
            setIsDone(true);
        })
    }

    useEffect(() => {
        const unloadCallback = (event) => {
            if (isCreating) {
                event.preventDefault();
                event.returnValue = "Image creation will be stopped";
                return "Image creation will be stopped";
            }
        };
        window.addEventListener("beforeunload", unloadCallback);
        return () => window.removeEventListener("beforeunload", unloadCallback);
    }, [isCreating]);

    useEffect(() => {
        if (!open) {
            const lastDoneRow = rows.findLast(row => !!row.data);
            props?.onClose?.({ isDone, data: lastDoneRow?.data });
        }
    }, [open, props, props.onClose]);

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={() => { }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-950 dark:bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full flex flex-col relative transform rounded-lg bg-ggray-100 dark:bg-gray-800 dark:text-gray-100 px-4 pt-2 pb-2 text-left shadow-xl transition-all sm:my-2 sm:w-full sm:max-w-4xl sm:p-6 min-h-[440px]">
                                <div className="text-center text-gray-900 dark:text-gray-100 my-2 font-medium text-lg">
                                    Bulk Create
                                </div>
                                <div className="text-center text-gray-500 dark:text-gray-400 text-sm px-2 sm:px-8 mt-2">
                                    Define multiple prompts to create images in bulk
                                </div>
                                <div className="flex-1">
                                    <div className="flex w-full flex-col justify-center mt-4 sm:mt-6 px-2 sm:px-8 flex-1">
                                        {rows.map((row, i) => (
                                            <InputRow
                                                {...row}
                                                key={i}
                                                position={i}
                                                disabled={isCreating}
                                                isCreating={isCreating}
                                                deleteRow={() => {
                                                    setRows([...rows].splice(i, 1));
                                                }}
                                                setPrompt={(prompt) => {
                                                    const newRows = [...rows];
                                                    newRows[i].prompt = prompt;
                                                    setRows(newRows);
                                                }}
                                                setSelectedMode={(mode, auxMode) => {
                                                    const newRows = [...rows];
                                                    newRows[i].mode = mode;
                                                    newRows[i].auxMode = auxMode;
                                                    setRows(newRows);
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <div>
                                        {rows.length < 5 && <div className="px-2 sm:px-8 mt-2 sm:mt-3">
                                            <button disabled={isCreating} onClick={() => setRows([...rows, { ...rows[rows.length - 1] } || initialRow])} className="flex text-gray-900 dark:text-gray-100 px-4 py-2 text-left text-sm hover:bg-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-700 items-center bg-white dark:bg-gray-700 dark:disabled:text-gray-400 rounded-md border dark:border-0 dark:hover:bg-gray-600 shadow-sm font-medium">
                                                <PlusIcon className="w-4 h-4 -ml-1 mr-2" />
                                                <div>Add Prompt</div>
                                            </button>
                                        </div>}
                                    </div>
                                </div>
                                <div className="text-left text-gray-700 dark:text-gray-100 text-sm px-2 sm:px-16 mt-4 sm:mt-6 font-medium justify-center items-center flex flex-col">
                                    <div>Images are created one at a time. Keep your browser open until all images are created.</div>
                                    <div className="mt-6"><div className="inline-flex p-2 px-3 rounded border bg-white dark:bg-gray-700 dark:border-gray-600 items-center">
                                        <input defaultChecked={true} onChange={(evt) => {
                                            sendNotificationRef.current = evt.target.checked;
                                        }} type="checkbox" id="notify-checkbox" className="mr-2" />
                                        <label htmlFor="notify-checkbox">Send me an Email when done</label>
                                    </div>
                                    </div>
                                </div>
                                <div className="mt-4 sm:mt-8 flex justify-center items-center sm:-ml-28">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center px-4 py-2 text-base font-medium text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-100 border border-transparent rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 mr-6"
                                        onClick={() => setOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    {isDone ? (
                                        <button
                                            className="inline-flex items-center bg-green-600 hover:bg-green-700 disabled:bg-gray-500 justify-center rounded-md border border-transparent px-5 py-1 lg:py-2 text-base font-medium text-white shadow-sm  focus:outline-none sm:col-start-2"
                                            onClick={(e) => {
                                                setOpen(false);
                                            }}
                                        >
                                            <div className="flex-shrink w-5 h-5 mr-2">
                                                <CheckIcon />
                                            </div>
                                            Done
                                        </button>
                                    ) : (
                                        <button
                                            disabled={isCreating}
                                            className="inline-flex items-center bg-secondary-dark hover:bg-secondary-darkest disabled:bg-gray-500 justify-center rounded-md border border-transparent px-5 py-1 lg:py-2 text-base font-medium text-white shadow-sm  focus:outline-none sm:col-start-2"
                                            onClick={(e) => {
                                                go();
                                            }}
                                        >
                                            {isCreating ? (
                                                <div className="flex-shrink w-6 h-6">
                                                    <Spinner />
                                                </div>
                                            ) : (

                                                <svg
                                                    viewBox="0 0 20.001711 20.001542"
                                                    className="w-4 h-3 mr-2"
                                                    x="0px"
                                                    y="0px"
                                                    fill="currentColor"
                                                    version="1.1"
                                                    width="20.001711"
                                                    height="20.001543"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                    <g
                                                        transform="translate(3.4893159e-4,4.7922182e-4)">
                                                        <g
                                                            data-name="Layer 1"
                                                            id="g1">
                                                            <path d="m 20,10.27 v 8.34 A 1.33,1.33 0 0 1 18.65,20 H 10.31 A 1,1 0 0 1 9.69,19.82 1.78,1.78 0 0 1 9.24,19.46 1.28,1.28 0 0 1 9,18.61 v -2.24 h 5.2 a 2.22,2.22 0 0 0 2.24,-2.24 V 8.92 h 2.24 A 1.33,1.33 0 0 1 20,10.27 Z M 9.64,15.52 h 4.49 a 1.32,1.32 0 0 0 1.34,-1.35 V 5.83 A 1.32,1.32 0 0 0 14.13,4.48 H 11.88 V 9.69 A 2.12,2.12 0 0 1 11.31,11.16 3.62,3.62 0 0 1 8.5,12 H 7.2 4.44 v 2.24 a 1.32,1.32 0 0 0 1.34,1.35 H 9.64 Z M 8.07,10.94 H 9.69 A 1.22,1.22 0 0 0 10.58,10.58 1.38,1.38 0 0 0 10.81,10.31 1.47,1.47 0 0 0 11,9.64 V 1.3 A 1.3,1.3 0 0 0 10.82,0.63 1.38,1.38 0 0 0 10.59,0.36 1.22,1.22 0 0 0 9.69,0 H 1.35 A 1.32,1.32 0 0 0 0,1.35 V 9.6 a 1.32,1.32 0 0 0 1.35,1.34 z" />
                                                        </g>
                                                    </g>
                                                </svg>
                                            )}
                                            Create
                                        </button>)}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
